import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Package, Calculator, FileText, BookOpen, TrendingUp, Brain, Shield, Key,
  ArrowRightLeft, AlertTriangle, Heart, Layers, ChevronDown,
  Menu, X, Check, ArrowRight, Pickaxe, HardHat, Factory, Building, UtensilsCrossed, ShoppingBag, Sparkles, Clock, Zap
} from "lucide-react";

const DOMAIN = "stratumassetreg.com";
const EMAIL = `info@${DOMAIN}`;

function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  if (typeof window !== "undefined") {
    window.addEventListener("scroll", () => setScrolled(window.scrollY > 60), { passive: true });
  }
  const links = [
    { label: "How It Works", href: "#how" }, { label: "Who It's For", href: "#who" },
    { label: "Pricing", href: "#pricing" }, { label: "Security", href: "#security" },
  ];
  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#F8F5EF]/95 backdrop-blur-xl border-b border-[#E8E2D5] shadow-sm" : "bg-transparent"}`} data-testid="landing-nav">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <a href="#" className="flex items-center gap-2.5">
          <img src="/assets/stratum_logomark.png" alt="Stratum" className="w-8 h-8 rounded-lg" />
          <span className="font-display text-lg font-semibold text-[#0B1020]">Stratum</span>
        </a>
        <div className="hidden md:flex items-center gap-8">
          {links.map(l => <a key={l.href} href={l.href} className="text-sm text-[#6B7280] hover:text-[#0B1020] transition-colors">{l.label}</a>)}
          <Link to="/login" className="text-sm text-[#6B7280] hover:text-[#0B1020]">Sign in</Link>
          <Link to="/register" className="bg-[#0B1020] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[#1A233A] hover:text-[#C8A961] transition-all" data-testid="nav-cta">Start free trial</Link>
        </div>
        <button onClick={() => setOpen(!open)} className="md:hidden p-2"><Menu size={20} /></button>
      </div>
      {open && (
        <div className="md:hidden bg-white border-t border-[#E8E2D5] p-6 space-y-4">
          {links.map(l => <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="block text-sm py-2">{l.label}</a>)}
          <Link to="/login" className="block text-sm py-2">Sign in</Link>
          <Link to="/register" className="block bg-[#0B1020] text-white px-5 py-3 rounded-lg text-sm font-medium text-center">Start free trial</Link>
        </div>
      )}
    </nav>
  );
}

/* ──── Reusable product screenshot mockup ──── */
function AppScreenshot({ title, children, className = "" }) {
  return (
    <div className={`bg-[#0B1020] rounded-xl overflow-hidden shadow-2xl shadow-[#0B1020]/20 ${className}`}>
      <div className="flex items-center gap-1.5 px-4 py-2.5 bg-[#0B1020] border-b border-white/5">
        <div className="w-2.5 h-2.5 rounded-full bg-[#991B1B]/70" />
        <div className="w-2.5 h-2.5 rounded-full bg-[#C8A961]/70" />
        <div className="w-2.5 h-2.5 rounded-full bg-[#065F46]/70" />
        {title && <span className="text-[10px] text-white/25 ml-2">{title}</span>}
      </div>
      <div className="bg-[#1A233A] p-4">{children}</div>
    </div>
  );
}

function Hero() {
  return (
    <section className="pt-24 pb-16 px-6 bg-[#F8F5EF]" data-testid="hero-section">
      <div className="max-w-7xl mx-auto text-center">
        <p className="text-[11px] uppercase tracking-[0.2em] text-[#C8A961] font-medium mb-5">For every business — from sole props to JSE-listed groups</p>
        <h1 className="font-display text-4xl md:text-6xl font-semibold text-[#0B1020] leading-[1.08] mb-5 max-w-4xl mx-auto">
          Stop wasting time on your spreadsheet FAR.
        </h1>
        <p className="text-lg text-[#6B7280] max-w-2xl mx-auto mb-8">
          Import once. Depreciate automatically. Close in minutes. Stratum handles your fixed assets so you can stop re-typing and start signing off.
        </p>
        <div className="flex flex-wrap justify-center gap-4 mb-4">
          <Link to="/register" className="bg-[#0B1020] text-white px-8 py-3.5 rounded-lg text-sm font-medium hover:bg-[#1A233A] hover:text-[#C8A961] transition-all" data-testid="hero-cta">
            Start free 3-month trial
          </Link>
          <a href={`mailto:${EMAIL}?subject=Demo`} className="text-sm text-[#0B1020] font-medium flex items-center gap-2 px-4 py-3.5 hover:text-[#C8A961]">
            Book a demo <ArrowRight size={14} />
          </a>
        </div>
        <p className="text-xs text-[#6B7280] mb-12">No credit card. Free tier forever. Paid from R 450/mo.</p>

        {/* Product screenshot */}
        <div className="max-w-5xl mx-auto">
          <AppScreenshot title="stratumassetreg.com — Dashboard">
            <div className="grid grid-cols-4 gap-3 mb-3">
              {[["R 117M", "Carrying"], ["R 201M", "Cost"], ["(R 84M)", "Accum Dep"], ["R 1.1M", "Monthly Dep"]].map(([v, l]) => (
                <div key={l} className="bg-[#0B1020] rounded-lg p-3 text-center">
                  <div className="font-mono text-sm md:text-lg font-semibold text-white tabular-nums">{v}</div>
                  <div className="text-[9px] text-white/30 mt-1">{l}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#0B1020] rounded-lg p-3">
                <div className="text-[9px] text-white/30 mb-2">Carrying by Category</div>
                {["Land & Buildings", "Plant & Machinery", "Furniture"].map((c, i) => (
                  <div key={c} className="flex items-center gap-2 mb-1">
                    <div className="h-2 rounded-full bg-[#C8A961]" style={{ width: `${[70, 30, 20][i]}%` }} />
                    <span className="text-[8px] text-white/40">{c}</span>
                  </div>
                ))}
              </div>
              <div className="bg-[#0B1020] rounded-lg p-3 flex items-center justify-center">
                <div className="text-center">
                  <div className="font-mono text-3xl font-bold text-[#C8A961]">64</div>
                  <div className="text-[9px] text-white/30">Health Score</div>
                </div>
              </div>
            </div>
          </AppScreenshot>
        </div>

        <div className="flex justify-center gap-8 mt-8 text-[10px] uppercase tracking-[0.2em] text-[#C8A961]/40">
          <span>IFRS</span><span>King IV</span><span>POPIA</span><span>SARS</span><span>IAS 16</span>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="py-20 px-6 bg-[#FAF8F4]" id="how" data-testid="how-section">
      <div className="max-w-6xl mx-auto">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#C8A961] font-medium mb-3 text-center">How It Works</p>
        <h2 className="font-display text-3xl md:text-4xl font-semibold text-[#0B1020] mb-4 text-center">Three steps. Then it runs itself.</h2>
        <p className="text-[#6B7280] text-center max-w-xl mx-auto mb-14">Set up once. Stratum automates your depreciation, journals, and reporting from then on.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: Zap, n: "1", title: "Import", desc: "Upload your spreadsheet. Stratum maps columns, creates entities, and goes live in 30 minutes.", tag: "30 min setup" },
            { icon: Clock, n: "2", title: "Automate", desc: "One click runs depreciation. Journals post. AI flags issues. You review — the system works.", tag: "Monthly close: 30 min" },
            { icon: FileText, n: "3", title: "Report", desc: "AFS note, policy document, auditor pack, board pack — generated live from your data.", tag: "Year-end: 1 day" },
          ].map(s => (
            <div key={s.n} className="text-center">
              <div className="w-14 h-14 rounded-2xl bg-[#0B1020] flex items-center justify-center mx-auto mb-4">
                <s.icon size={24} className="text-[#C8A961]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-xl font-semibold text-[#0B1020] mb-2">{s.title}</h3>
              <p className="text-sm text-[#6B7280] mb-3">{s.desc}</p>
              <span className="text-xs font-medium text-[#C8A961]">{s.tag}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductViews() {
  return (
    <section className="py-20 px-6 bg-[#F8F5EF]">
      <div className="max-w-6xl mx-auto">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#C8A961] font-medium mb-3 text-center">See It In Action</p>
        <h2 className="font-display text-3xl font-semibold text-[#0B1020] mb-12 text-center">Built for the way finance teams actually work.</h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* AFS Note */}
          <div>
            <AppScreenshot title="Full FAR — AFS Note (IAS 16.73)">
              <div className="space-y-1">
                <div className="flex text-[8px] text-white/40 gap-1 pb-1 border-b border-white/10">
                  <span className="flex-1">Category</span><span className="w-16 text-right">Cost</span><span className="w-16 text-right">Accum Dep</span><span className="w-16 text-right font-semibold">Carrying</span>
                </div>
                {[["Land & Buildings", "R 88.5M", "R 5.3M", "R 83.2M"], ["Furniture", "R 36.2M", "R 28.4M", "R 7.7M"], ["Plant & Machinery", "R 23.9M", "R 13.8M", "R 10.2M"], ["Motor Vehicles", "R 2.0M", "R 1.2M", "R 0.9M"], ["Office Equipment", "R 9.5M", "R 8.8M", "R 0.6M"]].map(([c, co, ad, ca]) => (
                  <div key={c} className="flex text-[9px] text-white/60 gap-1 py-1 border-b border-white/5">
                    <span className="flex-1 text-white/80">{c}</span><span className="w-16 text-right font-mono tabular-nums">{co}</span><span className="w-16 text-right font-mono tabular-nums text-red-400/60">{ad}</span><span className="w-16 text-right font-mono tabular-nums text-white font-semibold">{ca}</span>
                  </div>
                ))}
                <div className="flex text-[9px] font-semibold text-[#C8A961] gap-1 pt-1">
                  <span className="flex-1">Total</span><span className="w-16 text-right font-mono">R 201M</span><span className="w-16 text-right font-mono">R 84M</span><span className="w-16 text-right font-mono">R 117M</span>
                </div>
              </div>
            </AppScreenshot>
            <h3 className="font-display text-base font-semibold text-[#0B1020] mt-4 mb-1">AFS PPE Note — always reconciled.</h3>
            <p className="text-sm text-[#6B7280]">IAS 16.73 reconciliation generated live. Click any line to drill to the source.</p>
          </div>

          {/* AI Chat */}
          <div>
            <AppScreenshot title="Stratum AI — IFRS Expert">
              <div className="space-y-2">
                <div className="flex justify-end"><div className="bg-[#0B1020] text-white rounded-2xl rounded-br-sm px-3 py-2 text-[10px] max-w-[75%]">What SARS section applies to motor vehicles?</div></div>
                <div className="flex gap-2">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#C8A961] to-[#A08840] flex items-center justify-center flex-shrink-0 mt-0.5"><Sparkles size={10} className="text-white" /></div>
                  <div className="bg-white/10 rounded-2xl rounded-bl-sm px-3 py-2 text-[10px] text-white/80 max-w-[80%]">
                    Motor vehicles are written off under <span className="px-1 py-0.5 rounded bg-[#C8A961]/20 text-[#C8A961] text-[8px] font-mono font-semibold">S11(e)</span> per the BGR 7 Annexure: <strong>5 years (20% p.a.)</strong> for passenger vehicles, <strong>4 years (25% p.a.)</strong> for delivery vehicles. This typically differs from the <span className="px-1 py-0.5 rounded bg-[#C8A961]/20 text-[#C8A961] text-[8px] font-mono font-semibold">IAS 16</span> accounting life, creating a temporary difference and deferred tax per <span className="px-1 py-0.5 rounded bg-[#C8A961]/20 text-[#C8A961] text-[8px] font-mono font-semibold">IAS 12</span>.
                  </div>
                </div>
              </div>
            </AppScreenshot>
            <h3 className="font-display text-base font-semibold text-[#0B1020] mt-4 mb-1">AI that knows IFRS, SARS, and your data.</h3>
            <p className="text-sm text-[#6B7280]">Ask in plain English. Get answers with live web search and specific standard references.</p>
          </div>

          {/* Depreciation */}
          <div>
            <AppScreenshot title="Depreciation — One Click">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] text-white/60">Monthly charge: <span className="text-[#C8A961] font-semibold">R 1,136,987</span></div>
                <div className="bg-[#C8A961] text-[#0B1020] px-3 py-1 rounded text-[9px] font-semibold">Run Depreciation</div>
              </div>
              {["LB-0001  Head Office  R 7,500/mo", "FF-0001  Furniture Set  R 1,979/mo", "PM-0001  CNC Machine  R 11,250/mo"].map(l => (
                <div key={l} className="text-[9px] text-white/50 py-1 border-b border-white/5 font-mono">{l}</div>
              ))}
            </AppScreenshot>
            <h3 className="font-display text-base font-semibold text-[#0B1020] mt-4 mb-1">Depreciation on autopilot.</h3>
            <p className="text-sm text-[#6B7280]">Monthly and annual charges calculated. Journals post with one click. CSV exports to any accounting system.</p>
          </div>

          {/* Health Score */}
          <div>
            <AppScreenshot title="Compliance Health Score">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="font-mono text-4xl font-bold text-[#C8A961]">64</div>
                  <div className="text-[9px] text-white/30">/ 100 (C)</div>
                </div>
                <div className="flex-1 space-y-2">
                  {[["Data Integrity", 19, 25, "#065F46"], ["Dep Recon", 0, 25, "#991B1B"], ["Period Control", 25, 25, "#065F46"], ["Policy", 20, 25, "#C8A961"]].map(([l, s, m, c]) => (
                    <div key={l}>
                      <div className="flex justify-between text-[8px] text-white/40 mb-0.5"><span>{l}</span><span style={{color: c}}>{s}/{m}</span></div>
                      <div className="h-1.5 bg-white/10 rounded-full"><div className="h-1.5 rounded-full" style={{ width: `${s/m*100}%`, backgroundColor: c }} /></div>
                    </div>
                  ))}
                </div>
              </div>
            </AppScreenshot>
            <h3 className="font-display text-base font-semibold text-[#0B1020] mt-4 mb-1">Know your compliance status at a glance.</h3>
            <p className="text-sm text-[#6B7280]">0-100 score across data integrity, depreciation recon, period controls, and policy review. One-click fixes.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoItsFor() {
  const personas = [
    { n: "1", title: "One-Person Business", price: "Free forever", desc: "Vehicle, equipment, laptop. Import once — done." },
    { n: "2", title: "Growing SME", price: "R 450/mo", desc: "Stop re-typing. Stratum talks to Sage and Xero." },
    { n: "3", title: "Finance Practice", price: "R 1,200/mo", desc: "Every client gets their own workspace. Same process." },
    { n: "4", title: "Multi-Entity Group", price: "R 8,900/mo", desc: "Consolidate in real time. One source of truth." },
    { n: "5", title: "JSE-Listed", price: "Enterprise", desc: "SOC 2-ready. BYOK. SIEM. Pre-approved for procurement." },
  ];
  return (
    <section className="py-20 px-6 bg-[#FAF8F4]" id="who" data-testid="who-section">
      <div className="max-w-5xl mx-auto text-center">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#C8A961] font-medium mb-3">Who It's For</p>
        <h2 className="font-display text-3xl md:text-4xl font-semibold text-[#0B1020] mb-4">5 assets or 5,000. Same platform.</h2>
        <p className="text-[#6B7280] mb-12 max-w-lg mx-auto">From a sole prop with a van to a JSE-listed group with thousands of assets.</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {personas.map(p => (
            <div key={p.n} className="bg-white rounded-xl border border-[#E8E2D5] p-4 hover:shadow-md hover:border-[#C8A961]/30 transition-all text-center" data-testid={`persona-${p.n}`}>
              <div className="w-10 h-10 rounded-full bg-[#0B1020] flex items-center justify-center mx-auto mb-3">
                <span className="text-[#C8A961] font-mono text-sm font-bold">{p.n}</span>
              </div>
              <h3 className="font-display text-sm font-semibold text-[#0B1020] mb-1">{p.title}</h3>
              <p className="text-[11px] text-[#6B7280] mb-2">{p.desc}</p>
              <span className="text-[10px] font-medium text-[#C8A961]">{p.price}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features() {
  const feats = [
    { icon: Package, title: "Multi-entity FAR", desc: "One workspace. Every subsidiary. Group consolidation." },
    { icon: Calculator, title: "Auto depreciation", desc: "One click. Balanced journals. CSV to any system." },
    { icon: Brain, title: "AI assistant", desc: "IFRS, SARS, and your data. Live web search." },
    { icon: TrendingUp, title: "Capex approvals", desc: "3 quotes. MFA chains. WIP. Capitalisation." },
    { icon: BookOpen, title: "Tax + deferred tax", desc: "Parallel SARS register. 27% auto-computed." },
    { icon: Shield, title: "Auditor-ready", desc: "One-click pack. Every journal drill-through." },
  ];
  return (
    <section className="py-16 px-6 bg-[#F8F5EF]" id="features">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {feats.map(f => (
            <div key={f.title} className="bg-white rounded-xl border border-[#E8E2D5] p-4 hover:shadow-sm transition-shadow text-center">
              <f.icon size={20} className="text-[#C8A961] mx-auto mb-2" strokeWidth={1.5} />
              <h3 className="font-display text-xs font-semibold text-[#0B1020] mb-1">{f.title}</h3>
              <p className="text-[10px] text-[#6B7280] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Numbers() {
  return (
    <section className="py-14 px-6 bg-[#0B1020]" data-testid="numbers-section">
      <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
        {[["96 hrs", "saved per year"], ["1 day", "year-end (was 3 weeks)"], ["1 click", "any audit answer"], ["R 380k", "avg annual saving"]].map(([n, l]) => (
          <div key={l}>
            <div className="font-display text-3xl md:text-4xl font-bold text-[#C8A961] tabular-nums">{n}</div>
            <p className="text-xs text-white/40 mt-1">{l}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Industries() {
  const mods = [
    { icon: Pickaxe, name: "Mining" }, { icon: HardHat, name: "Construction" },
    { icon: Factory, name: "Manufacturing" }, { icon: Building, name: "Real Estate" },
    { icon: UtensilsCrossed, name: "Hospitality" }, { icon: ShoppingBag, name: "Retail" },
  ];
  return (
    <section className="py-16 px-6 bg-[#FAF8F4]" id="industries">
      <div className="max-w-4xl mx-auto text-center">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#C8A961] font-medium mb-3">Industry Modules</p>
        <h2 className="font-display text-2xl font-semibold text-[#0B1020] mb-8">Six sectors. Activate per entity.</h2>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {mods.map(m => (
            <div key={m.name} className="bg-white rounded-xl border border-[#E8E2D5] p-4 text-center hover:border-[#C8A961]/30 transition-colors">
              <m.icon size={20} className="text-[#C8A961] mx-auto mb-2" strokeWidth={1.5} />
              <span className="text-xs font-medium text-[#0B1020]">{m.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Security() {
  return (
    <section className="py-16 px-6 bg-[#F8F5EF]" id="security" data-testid="security-section">
      <div className="max-w-4xl mx-auto text-center">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#C8A961] font-medium mb-3">Security & Compliance</p>
        <h2 className="font-display text-2xl font-semibold text-[#0B1020] mb-8">Enterprise-grade. SA-hosted. Audit-ready.</h2>
        <div className="flex flex-wrap justify-center gap-3">
          {["AES-256 encryption", "TLS 1.3", "POPIA compliant", "Data in Cape Town", "RBAC (8 roles)", "Immutable audit trail", "SOC 2-ready", "BYOK option", "King IV attestations", "Period locking"].map(s => (
            <span key={s} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E8E2D5] text-xs text-[#1A1A1A]">
              <Check size={10} className="text-[#065F46]" />{s}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const tiers = [
    { name: "Free", price: "R 0", target: "Up to 50 assets", features: ["1 entity", "Core FAR", "CSV export"], cta: "Start free", style: "text" },
    { name: "Solo", price: "R 450", target: "Single-entity SME", features: ["500 assets", "AFS Note", "5 AI/mo"], cta: "Start trial", style: "navy" },
    { name: "Studio", price: "R 1,200", target: "Finance practices", features: ["Unlimited assets", "Capex workflow", "25 AI/mo"], cta: "Start trial", style: "navy" },
    { name: "Growth", price: "R 2,900", target: "2-5 entities", features: ["5 entities", "Auditor Pack", "100 AI/mo"], cta: "Start trial", style: "navy" },
    { name: "Group", price: "R 8,900", target: "5-20 entities", pop: true, features: ["Unlimited entities", "Tax + DT engine", "All modules", "500 AI/mo"], cta: "Start trial", style: "gold" },
    { name: "Enterprise", price: "R 35k+", target: "JSE-listed", features: ["SSO + SAML", "BYOK", "SIEM", "Dedicated CSM"], cta: "Contact sales", style: "text" },
  ];
  return (
    <section className="py-20 px-6 bg-[#FAF8F4]" id="pricing" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#C8A961] font-medium mb-3 text-center">Pricing</p>
        <h2 className="font-display text-3xl font-semibold text-[#0B1020] mb-3 text-center">Per entity, not per asset.</h2>
        <p className="text-sm text-[#6B7280] mb-10 text-center">3-month free trial on all paid plans. Annual saves 20%.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {tiers.map(t => (
            <div key={t.name} className={`bg-white rounded-xl border p-4 flex flex-col relative ${t.pop ? "border-[#C8A961] ring-2 ring-[#C8A961]/20" : "border-[#E8E2D5]"}`}>
              {t.pop && <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#C8A961] text-white text-[8px] uppercase tracking-widest px-2 py-0.5 rounded-full font-medium">Popular</div>}
              <h3 className="font-display text-base font-semibold text-[#0B1020]">{t.name}</h3>
              <div className="mt-1 mb-1"><span className="font-display text-xl font-bold text-[#0B1020]">{t.price}</span><span className="text-[10px] text-[#6B7280]">{t.name !== "Free" && t.name !== "Enterprise" ? "/mo" : ""}</span></div>
              <p className="text-[10px] text-[#6B7280] mb-3">{t.target}</p>
              <ul className="flex-1 space-y-1 mb-3">{t.features.map(f => <li key={f} className="text-[10px] text-[#6B7280] flex items-start gap-1"><Check size={9} className="text-[#065F46] mt-0.5" />{f}</li>)}</ul>
              {t.style === "text" ? (
                <a href={t.name === "Enterprise" ? `mailto:${EMAIL}` : "/register"} className="text-xs text-[#0B1020] font-medium flex items-center justify-center gap-1 hover:text-[#C8A961]">{t.cta} <ArrowRight size={10} /></a>
              ) : (
                <Link to="/register" className={`text-center py-2 rounded-lg text-xs font-medium ${t.style === "gold" ? "bg-[#C8A961] text-[#0B1020]" : "bg-[#0B1020] text-white hover:text-[#C8A961]"}`}>{t.cta}</Link>
              )}
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-[#6B7280] mt-6">A typical 5-entity group spends R 380k/yr on PPE consulting. Group tier: R 107k/yr. <strong className="text-[#0B1020]">3.5x return.</strong></p>
      </div>
    </section>
  );
}

function FAQ() {
  const [open, setOpen] = useState(null);
  const qs = [
    ["How long to migrate from Excel?", "30 minutes. Upload CSV, Stratum maps columns and categories automatically. 30-day rollback."],
    ["How do journals get into my accounting system?", "Stratum exports journals as CSV formatted specifically for your system — 7 built-in formats for Sage Pastel, Sage Evolution, Xero, QuickBooks, Dynamics 365, Odoo, and a generic format. Download the CSV, import into your system. Direct API integration with Xero and Sage is on the roadmap."],
    ["Is data hosted in South Africa?", "Cape Town by default. Full POPIA compliance with data export and deletion rights."],
    ["Can auditors get read-only access?", "Yes. Auditor role is free — full visibility, zero mutation."],
    ["How does the AI work?", "Claude with live web search for current tax law updates. Cites specific IFRS paragraphs, SARS BGR 7 references, and knows your actual data."],
    ["Is Stratum SOC 2 certified?", "In audit progress. Evidence pack available under NDA for procurement."],
  ];
  return (
    <section className="py-16 px-6 bg-[#F8F5EF]" data-testid="faq-section">
      <div className="max-w-2xl mx-auto">
        <h2 className="font-display text-2xl font-semibold text-[#0B1020] mb-8 text-center">FAQ</h2>
        <div className="space-y-2">
          {qs.map(([q, a], i) => (
            <div key={i} className="bg-white rounded-xl border border-[#E8E2D5] overflow-hidden">
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full text-left px-4 py-3 flex items-center justify-between" data-testid={`faq-${i}`}>
                <span className="text-sm font-medium text-[#0B1020] pr-4">{q}</span>
                <ChevronDown size={14} className={`text-[#6B7280] flex-shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && <div className="px-4 pb-3 text-sm text-[#6B7280] border-t border-[#E8E2D5] pt-2">{a}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-20 px-6 bg-[#0B1020] text-center" data-testid="final-cta">
      <div className="max-w-xl mx-auto">
        <h2 className="font-display text-3xl font-semibold text-[#F8F5EF] mb-3">Done with the spreadsheet?</h2>
        <p className="text-white/40 mb-8">3 months free. 30-minute setup. No credit card.</p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/register" className="bg-[#C8A961] text-[#0B1020] px-8 py-3.5 rounded-lg text-sm font-semibold hover:bg-[#B69A51]" data-testid="final-cta-btn">Start free trial</Link>
          <a href={`mailto:${EMAIL}?subject=Demo`} className="text-sm text-white/50 flex items-center gap-2 px-4 py-3.5 hover:text-[#C8A961]">Book a demo <ArrowRight size={14} /></a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="py-12 px-6 bg-[#0B1020] border-t border-white/5" data-testid="footer">
      <div className="max-w-5xl mx-auto flex flex-wrap justify-between gap-8 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <img src="/assets/stratum_logomark.png" alt="Stratum" className="w-7 h-7 rounded-lg" />
            <span className="font-display text-sm font-semibold text-white">Stratum</span>
          </div>
          <p className="text-[10px] text-white/25">{EMAIL}</p>
        </div>
        <div className="flex gap-10 text-xs text-white/40">
          <div className="space-y-2"><a href="#how" className="block hover:text-[#C8A961]">How It Works</a><a href="#who" className="block hover:text-[#C8A961]">Who It's For</a><a href="#pricing" className="block hover:text-[#C8A961]">Pricing</a></div>
          <div className="space-y-2"><a href="#security" className="block hover:text-[#C8A961]">Security</a><a href="#faq" className="block hover:text-[#C8A961]">FAQ</a><a href={`mailto:${EMAIL}`} className="block hover:text-[#C8A961]">Contact</a></div>
        </div>
      </div>
      <div className="max-w-5xl mx-auto border-t border-white/5 pt-4 flex justify-between text-[9px] text-white/15">
        <span>2026 Stratum. All rights reserved.</span>
        <span>Terms &middot; Privacy &middot; POPIA</span>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  return (
    <div className="bg-[#F8F5EF]" data-testid="landing-page">
      <Nav />
      <Hero />
      <HowItWorks />
      <ProductViews />
      <WhoItsFor />
      <Features />
      <Numbers />
      <Industries />
      <Security />
      <Pricing />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}
